import React, { PureComponent } from 'react';
import '@aerialist7/react-dates/initialize';
import PropTypes from 'prop-types';
import moment from 'moment';
import { DayPickerRangeController, isInclusivelyAfterDay, isSameDay } from '@aerialist7/react-dates';
import { START_DATE, END_DATE } from '@aerialist7/react-dates/constants';

import { SVGIcon, Adaptive, colors } from '@custom-components';

import * as Styled from './LimitedDatePicker.styles';
import { ReactChildrenPropType } from '../../../lib/commonPropTypes';

class DatePicker extends PureComponent {
  static LL_DATE_FORMAT = 'YYYY-MM-DD';

  static PRETTY_DATE_FORMAT = 'MMM DD, YYYY';

  static resolveIncomingDate(dateString) {
    if (typeof dateString !== 'string') {
      return null;
    }

    if (dateString instanceof moment) {
      return dateString;
    }

    const momentDate = moment(dateString, DatePicker.LL_DATE_FORMAT);
    return momentDate.isValid() ? momentDate : null;
  }

  constructor(props) {
    super(props);
    this.state = {
      focusedInput: props.initialStartDate && !props.initialEndDate ? END_DATE : START_DATE,
      startDate: DatePicker.resolveIncomingDate(props.initialStartDate),
      endDate: DatePicker.resolveIncomingDate(props.initialEndDate),
      visible: props.open
    };

    this.createWrapperNode = this.createWrapperNode.bind(this);
    this.closeDayPicker = this.closeDayPicker.bind(this);
    this.onDatesChange = this.onDatesChange.bind(this);
    this.onFocusChange = this.onFocusChange.bind(this);
    this.openDayPicker = this.openDayPicker.bind(this);
    this.handleClickListener = this.handleClickListener.bind(this);
    this.outsideRange = this.outsideRange.bind(this);
    this.resolveDateStrings = this.resolveDateStrings.bind(this);
    this.resolveBlockedDays = this.resolveBlockedDays.bind(this);
  }

  componentDidMount() {
    if (document) {
      document.addEventListener('mousedown', this.handleClickListener, true);
    }
  }

  componentDidUpdate(prevProps) {
    const { initialStartDate, initialEndDate } = this.props;
    if (initialStartDate !== prevProps.initialStartDate || initialEndDate !== prevProps.initialEndDate)
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        startDate: DatePicker.resolveIncomingDate(initialStartDate),
        endDate: DatePicker.resolveIncomingDate(initialEndDate)
      });
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickListener, true);
  }

  handleClickListener({ target }) {
    if (!this?.node?.contains) {
      return false;
    }

    if (this.node.contains(target)) {
      return false;
    }

    this.closeDayPicker();
    return true;
  }

  onFocusChange(focusedInput) {
    this.setState({
      focusedInput: !focusedInput ? START_DATE : focusedInput
    });
  }

  onDatesChange({ startDate, endDate }) {
    const { onDatesChangeHook } = this.props;
    this.setState(
      ({ startDate: currStartDate, endDate: currEndDate }) => {
        if (startDate && !startDate.isSame(currStartDate) && currEndDate) {
          return { startDate, endDate: null };
        }
        return { startDate, endDate };
      },
      () => {
        const resolvedDates = this.resolveDateStrings();
        const { startDate: newStartDate, endDate: newEndDate } = this.state;
        if (newStartDate && newEndDate) this.closeDayPicker();
        onDatesChangeHook(resolvedDates, this.resolveDateStrings);
      }
    );
  }

  // Note: eslint is added as this function is being triggered from parent component
  // but this component is treating this function as unused.
  // Temporarily disabling eslint for this class.
  // This can be avoid once converting this component to functional component.
  // and wrapping this component with forwardRef and using useImperativeHandle
  // eslint-disable-next-line react/no-unused-class-component-methods
  resetDates() {
    this.setState({
      startDate: null,
      endDate: null
    });
  }

  createWrapperNode(node) {
    this.node = node;
  }

  outsideRange(day) {
    const { limitedRange, isOutsideRange } = this.props;
    const { focusedInput, startDate } = this.state;
    if (isOutsideRange) {
      return isOutsideRange(day);
    }
    if (limitedRange) {
      if (focusedInput === END_DATE) {
        return (
          !isInclusivelyAfterDay(day, startDate) ||
          isInclusivelyAfterDay(day, moment(startDate).add(limitedRange, 'days'))
        );
      }
      return false;
    }
    return !isInclusivelyAfterDay(day, moment());
  }

  resolveDateStrings(format = DatePicker.LL_DATE_FORMAT) {
    const { startDate, endDate } = this.state;
    return [startDate ? startDate.format(format) : undefined, endDate ? endDate.format(format) : undefined];
  }

  resolveBlockedDays(currDay) {
    const { blockedDays } = this.props;
    const verifyKeys = (i) =>
      Object.prototype.hasOwnProperty.call(i, 'start') && Object.prototype.hasOwnProperty.call(i, 'end');
    return blockedDays.some((day) => {
      if (typeof day === 'object' && verifyKeys(day)) {
        const start = DatePicker.resolveIncomingDate(day.start);
        const end = DatePicker.resolveIncomingDate(day.end);
        return currDay.isBetween(start, end);
      }
      return isSameDay(currDay, DatePicker.resolveIncomingDate(day));
    });
  }

  openDayPicker() {
    const { visible } = this.state;
    if (!visible) {
      this.setState({ visible: true });
    }
  }

  closeDayPicker() {
    const { visible } = this.state;
    if (visible) {
      this.setState(({ startDate: currStartDate, endDate: currEndDate }) => {
        if (!(currStartDate && currEndDate)) {
          return { visible: false, startDate: undefined, endDate: undefined, focusedInput: START_DATE };
        }
        return { visible: false };
      });
    }
  }

  /**
   * Render helper methods to clean up our render()
   */
  renderCalendarInfo = () => {
    const { disclaimerCopy } = this.props;
    return disclaimerCopy ? (
      <Styled.CopyMini color="tertiary" align="center">
        {disclaimerCopy}
      </Styled.CopyMini>
    ) : null;
  };

  render() {
    const {
      children,
      labelText,
      inputBorderType,
      keepOpenOnDateSelect,
      blockedDays,
      mobileDaySize,
      onDatesChangeHook,
      initialStartDate,
      initialEndDate,
      disclaimerCopy,
      open,
      fullScreen,
      pickerPosition,
      disableDatePicker,
      noArrow,
      navNext,
      limitedRange,
      isDayHighlighted,
      hideKeyboardShortcutsPanel,
      numberOfMonths,
      navPrev,
      monthFormat,
      minimumNights,
      'data-testid': testId
    } = this.props;
    const { focusedInput, startDate, endDate, visible } = this.state;
    const [startDateString, endDateString] = this.resolveDateStrings(DatePicker.PRETTY_DATE_FORMAT);
    const startDateNoYear = startDateString && startDateString.split(',')[0];
    const resolveEndDate = endDateString ? ` - ${endDateString}` : '';
    const inputValue = startDateNoYear ? `${startDateNoYear}${resolveEndDate}` : undefined;

    return (
      <Styled.Wrapper data-testid={testId} fullScreen={fullScreen} ref={this.createWrapperNode}>
        {children
          ? children({
              startDate,
              endDate,
              visible,
              inputValue,
              resolver: this.resolveDateStrings,
              openPicker: this.openDayPicker,
              closePicker: this.closeDayPicker
            })
          : null}
        <Styled.DayPickerWrapper
          visible={fullScreen ? true : visible}
          focusedInput={focusedInput}
          fullScreen={fullScreen}
          pickerPosition={pickerPosition}
          noArrow={noArrow}
        >
          <Adaptive
            md={
              <DayPickerRangeController
                key="lg-breakpoint-datepicker"
                blockedDays={blockedDays}
                disclaimerCopy={disclaimerCopy}
                disableDatePicker={disableDatePicker}
                initialStartDate={initialStartDate}
                initialEndDate={initialEndDate}
                fullScreen={fullScreen}
                labelText={labelText}
                onDatesChangeHook={onDatesChangeHook}
                open={open}
                pickerPosition={pickerPosition}
                noArrow={noArrow}
                limitedRange={limitedRange}
                numberOfMonths={numberOfMonths}
                isOutsideRange={this.outsideRange}
                onDatesChange={this.onDatesChange}
                onFocusChange={this.onFocusChange}
                renderCalendarInfo={this.renderCalendarInfo}
                isDayBlocked={(day) => (blockedDays ? this.resolveBlockedDays(day) : undefined)}
                focusedInput={focusedInput}
                startDate={startDate}
                endDate={endDate}
                mobileDaySize={mobileDaySize}
                isDayHighlighted={isDayHighlighted}
                keepOpenOnDateSelect={keepOpenOnDateSelect}
                hideKeyboardShortcutsPanel={hideKeyboardShortcutsPanel}
                navNext={navNext}
                navPrev={navPrev}
                monthFormat={monthFormat}
                inputBorderType={inputBorderType}
                minimumNights={minimumNights}
              />
            }
            sm={
              <DayPickerRangeController
                key="sm-breakpoint-datepicker"
                blockedDays={blockedDays}
                disclaimerCopy={disclaimerCopy}
                disableDatePicker={disableDatePicker}
                initialStartDate={initialStartDate}
                initialEndDate={initialEndDate}
                fullScreen={fullScreen}
                labelText={labelText}
                onDatesChangeHook={onDatesChangeHook}
                open={open}
                pickerPosition={pickerPosition}
                noArrow={noArrow}
                limitedRange={limitedRange}
                daySize={mobileDaySize || 30}
                numberOfMonths={1}
                isOutsideRange={this.outsideRange}
                onDatesChange={this.onDatesChange}
                onFocusChange={this.onFocusChange}
                renderCalendarInfo={this.renderCalendarInfo}
                isDayBlocked={(day) => (blockedDays ? this.resolveBlockedDays(day) : undefined)}
                focusedInput={focusedInput}
                startDate={startDate}
                endDate={endDate}
                mobileDaySize={mobileDaySize}
                isDayHighlighted={isDayHighlighted}
                keepOpenOnDateSelect={keepOpenOnDateSelect}
                hideKeyboardShortcutsPanel={hideKeyboardShortcutsPanel}
                navNext={navNext}
                navPrev={navPrev}
                monthFormat={monthFormat}
                inputBorderType={inputBorderType}
                minimumNights={minimumNights}
              />
            }
          />
        </Styled.DayPickerWrapper>
      </Styled.Wrapper>
    );
  }
}

DatePicker.propTypes = {
  /* Array of blocked days: can be either LL Date strings or a range object with a start/end property with values that are also LL Dates. */
  blockedDays: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ start: PropTypes.string, end: PropTypes.string })])
  ),

  /* Disclaimer Copy for the bottom of the DatePicker */
  disclaimerCopy: PropTypes.string,

  /** Boolean to disable DatePicker */
  disableDatePicker: PropTypes.bool,

  /* LL Date (YYYY/MM/DD) format to seed intial start date. */
  initialStartDate: PropTypes.string,

  /* LL Date (YYYY/MM/DD) format to seed intial end date. */
  initialEndDate: PropTypes.string,

  /* Fullscreen Mode - this is mainly for the Map screen */
  fullScreen: PropTypes.bool,

  /* Label Text for the input field */
  labelText: PropTypes.string,

  /* Hook fired when onDatesChange is fired by the DayPickerRangeController  */
  onDatesChangeHook: PropTypes.func,

  /* Flag to toggle whether the DatePicker is visible on mount or not. Useful for the render prop implementation */
  open: PropTypes.bool,

  /* One of 'left', 'center' or 'right' to position the picker. Defaults to left. */
  pickerPosition: PropTypes.oneOf(['left', 'center', 'right']),

  /* Flag to toggle off the arrow on the top of the DatePicker pointing to the input field */
  noArrow: PropTypes.bool,

  /* number of days after start date to limit to */

  limitedRange: PropTypes.number,

  /* Below are default props for the react-dates implementation being defined */
  numberOfMonths: PropTypes.number,
  isOutsideRange: PropTypes.func,
  isDayHighlighted: PropTypes.func,
  keepOpenOnDateSelect: PropTypes.bool,
  hideKeyboardShortcutsPanel: PropTypes.bool,
  navNext: PropTypes.node,
  navPrev: PropTypes.node,
  monthFormat: PropTypes.string,
  children: ReactChildrenPropType,
  inputBorderType: PropTypes.string,
  mobileDaySize: PropTypes.number,
  minimumNights: PropTypes.number,
  'data-testid': PropTypes.string
};

DatePicker.defaultProps = {
  /**
   * Not to be confused with the onDatesChange prop for DayPicker, this prop is fired as a hook when the internal DatesChange method is executed
   * Currently returns dates in different either moment or JSDate formats depending on props.
   */
  onDatesChangeHook: () => {},

  /** Start and End Dates */
  initialStartDate: null,
  initialEndDate: null,

  // day presentation and interaction related props
  isDayHighlighted: () => false,
  isOutsideRange: null, // day => !isInclusivelyAfterDay(day, moment()),
  keepOpenOnDateSelect: false,

  // calendar presentation and interaction related props
  numberOfMonths: 2,
  hideKeyboardShortcutsPanel: true,
  limitedRange: null,

  // navigation related props
  navPrev: <SVGIcon selectedIcon="arrow" position="static" rotate={90} iconColor={colors.colorBlue} />,
  navNext: <SVGIcon selectedIcon="arrow" position="static" rotate={-90} iconColor={colors.colorBlue} />,

  // internationalization
  monthFormat: 'MMMM YYYY',
  disclaimerCopy: null,
  labelText: 'Dates',

  // new props
  open: false,
  fullScreen: false,
  blockedDays: [],
  pickerPosition: 'left',
  noArrow: false,
  disableDatePicker: false,

  children: () => null,
  inputBorderType: '',
  mobileDaySize: 30,
  minimumNights: 1,
  'data-testid': ''
};

export default DatePicker;
